import * as React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/layout/header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Headphones,
  Mail,
  Phone,
  MapPin,
  Clock,
  MessageSquare,
  FileText,
  BookOpen,
  Video,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Wifi,
  Database,
  Server,
  Bot,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useHealth } from '@/hooks'

export const Route = createFileRoute('/support')({
  component: SupportPage,
})

// Documentation links
const docLinks = [
  {
    title: 'User Guide',
    description: 'Panduan lengkap penggunaan sistem',
    icon: BookOpen,
    href: '#',
  },
  {
    title: 'Video Tutorials',
    description: 'Tutorial video step-by-step',
    icon: Video,
    href: '#',
  },
  {
    title: 'API Documentation',
    description: 'Technical API reference',
    icon: FileText,
    href: '#',
  },
  {
    title: 'Release Notes',
    description: 'Changelog dan updates terkini',
    icon: MessageSquare,
    href: '#',
  },
]

// System services
const systemServices = [
  { name: 'Web Application', icon: Server },
  { name: 'API Server', icon: Database },
  { name: 'AI Services', icon: Bot },
  { name: 'Database', icon: Database },
]

function SupportPage() {
  const { data: health, isPending: isHealthLoading } = useHealth()

  // Determine overall system status
  const systemStatus = health?.status === 'healthy' ? 'operational' : 'degraded'

  return (
    <div className="flex flex-col min-h-screen">
      <Header
        title="Support"
        subtitle="Get help and contact our team"
      />

      <main className="flex-1 p-3 sm:p-4 lg:p-6 space-y-6">
        {/* Top Section: Contact + System Status */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Headphones className="h-5 w-5" />
                Contact Support
              </CardTitle>
              <CardDescription>
                Hubungi kami untuk sebarang pertanyaan atau bantuan
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Contact Items */}
              <div className="space-y-3">
                <ContactItem
                  icon={Mail}
                  label="Email"
                  value="support@klinikamisi.com"
                  href="mailto:support@klinikamisi.com"
                />
                <ContactItem
                  icon={Phone}
                  label="WhatsApp"
                  value="+60 12-345 6789"
                  href="https://wa.me/60123456789"
                />
                <ContactItem
                  icon={MapPin}
                  label="HQ Address"
                  value="Amisi Clinic Shah Alam, Selangor"
                />
                <ContactItem
                  icon={Clock}
                  label="Support Hours"
                  value="Mon-Fri: 9:00 AM - 6:00 PM"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button className="flex-1">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Start Live Chat
                </Button>
                <Button variant="outline" className="flex-1">
                  <Mail className="h-4 w-4 mr-2" />
                  Send Email
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* System Status */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Wifi className="h-5 w-5" />
                    System Status
                  </CardTitle>
                  <CardDescription>
                    Current status of all services
                  </CardDescription>
                </div>
                <StatusBadge status={systemStatus} loading={isHealthLoading} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {systemServices.map((service) => (
                  <ServiceStatusItem
                    key={service.name}
                    name={service.name}
                    icon={service.icon}
                    status={systemStatus}
                    loading={isHealthLoading}
                  />
                ))}
              </div>

              {/* Last Updated */}
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-muted-foreground text-center">
                  Last checked: {new Date().toLocaleTimeString('en-MY')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Documentation Links */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Documentation & Resources
            </CardTitle>
            <CardDescription>
              Panduan dan dokumentasi untuk membantu anda
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {docLinks.map((doc) => (
                <a
                  key={doc.title}
                  href={doc.href}
                  className="flex items-start gap-3 p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors group"
                >
                  <div className="p-2 rounded-lg bg-icon-beige">
                    <doc.icon className="h-5 w-5 text-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <span className="font-medium text-foreground group-hover:text-primary transition-colors">
                        {doc.title}
                      </span>
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {doc.description}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Version Info */}
        <div className="text-center text-sm text-muted-foreground">
          <p>NurinV2 Version 2.0.2-beta</p>
          <p className="text-xs mt-1">
            Powered by Azure OpenAI + Neon PostgreSQL
          </p>
        </div>
      </main>
    </div>
  )
}

// Contact Item Component
function ContactItem({
  icon: Icon,
  label,
  value,
  href,
}: {
  icon: React.ElementType
  label: string
  value: string
  href?: string
}) {
  const content = (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
      <div className="p-2 rounded-lg bg-icon-beige">
        <Icon className="h-4 w-4 text-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="font-medium text-foreground truncate">{value}</p>
      </div>
      {href && <ExternalLink className="h-4 w-4 text-muted-foreground" />}
    </div>
  )

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    )
  }

  return content
}

// Status Badge Component
function StatusBadge({
  status,
  loading,
}: {
  status: 'operational' | 'degraded' | 'outage'
  loading: boolean
}) {
  if (loading) {
    return (
      <Badge variant="secondary" className="gap-1">
        <span className="h-2 w-2 rounded-full bg-muted-foreground animate-pulse" />
        Checking...
      </Badge>
    )
  }

  const config = {
    operational: {
      icon: CheckCircle2,
      label: 'All Systems Operational',
      variant: 'default' as const,
      className: 'bg-success text-success-foreground',
    },
    degraded: {
      icon: AlertTriangle,
      label: 'Partial Outage',
      variant: 'secondary' as const,
      className: 'bg-warning text-warning-foreground',
    },
    outage: {
      icon: XCircle,
      label: 'Major Outage',
      variant: 'destructive' as const,
      className: '',
    },
  }

  const cfg = config[status]
  const StatusIcon = cfg.icon

  return (
    <Badge variant={cfg.variant} className={cn('gap-1', cfg.className)}>
      <StatusIcon className="h-3 w-3" />
      {cfg.label}
    </Badge>
  )
}

// Service Status Item Component
function ServiceStatusItem({
  name,
  icon: Icon,
  status,
  loading,
}: {
  name: string
  icon: React.ElementType
  status: 'operational' | 'degraded' | 'outage'
  loading: boolean
}) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
      <div className="flex items-center gap-3">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">{name}</span>
      </div>
      {loading ? (
        <span className="h-3 w-3 rounded-full bg-muted-foreground animate-pulse" />
      ) : (
        <span
          className={cn(
            'h-3 w-3 rounded-full',
            status === 'operational' && 'bg-success',
            status === 'degraded' && 'bg-warning',
            status === 'outage' && 'bg-destructive'
          )}
        />
      )}
    </div>
  )
}
